import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cardApi } from "@/components/admin/TrainingskartenTab";
import { CodeReveal } from "@/components/admin/cards/CodeReveal";

const EMPTY = {
  customer_name: "", customer_email: "", customer_phone: "", package_type: "10er_karte",
  training_type: "einzeltraining_kind", payment_method: "bank_transfer", already_paid: false,
  paid_date: "", credits_used: 0, send_email: false, note: "",
};

const Sel = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label>{label}</Label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 w-full h-10 rounded-md bg-[#0A0A0A] border border-white/20 px-3 text-sm">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export const CardCreateDialog = ({ open, onClose, adminToken, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const close = () => { setF(EMPTY); setCode(null); onClose(); };

  const submit = async () => {
    setBusy(true);
    try {
      const d = await cardApi(adminToken, "/create", "POST", {
        ...f, customer_phone: f.customer_phone || null, paid_date: f.paid_date || null,
        credits_used: Number(f.credits_used) || 0, note: f.note || null,
      });
      toast.success(`Karte ${d.card.card_id} angelegt`);
      onCreated();
      if (d.code) setCode(d.code); else close();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="card-create-dialog">
        <DialogHeader><DialogTitle>Trainingskarte erfassen</DialogTitle></DialogHeader>
        {code ? <><CodeReveal code={code} /><Button onClick={close} className="w-full" data-testid="btn-card-create-done">Fertig</Button></> : (
          <div className="space-y-3 text-sm">
            <div><Label>Name</Label><Input value={f.customer_name} onChange={(e) => set("customer_name")(e.target.value)} className="mt-1 bg-[#0A0A0A]" data-testid="input-cc-name" /></div>
            <div><Label>E-Mail</Label><Input type="email" value={f.customer_email} onChange={(e) => set("customer_email")(e.target.value)} className="mt-1 bg-[#0A0A0A]" data-testid="input-cc-email" /></div>
            <div><Label>Telefon (optional)</Label><Input value={f.customer_phone} onChange={(e) => set("customer_phone")(e.target.value)} className="mt-1 bg-[#0A0A0A]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Karte" value={f.package_type} onChange={set("package_type")} testId="select-cc-package" options={[["5er_karte", "5er-Karte"], ["10er_karte", "10er-Karte"]]} />
              <Sel label="Training (60 Min.)" value={f.training_type} onChange={set("training_type")} testId="select-cc-training"
                options={[["einzeltraining_kind", "Einzeltraining Kind"], ["einzeltraining_erwachsen", "Einzeltraining Erwachsen"], ["torwarttraining", "Torwarttraining"]]} />
            </div>
            <Sel label="Zahlungsart" value={f.payment_method} onChange={set("payment_method")} testId="select-cc-payment"
              options={[["bank_transfer", "Überweisung"], ["cash", "Bar"], ["paypal", "PayPal"]]} />
            <label className="flex items-start gap-2 bg-white/5 rounded p-3">
              <input type="checkbox" checked={f.already_paid} onChange={(e) => set("already_paid")(e.target.checked)} className="mt-1" data-testid="checkbox-cc-paid" />
              <span>Bereits verkauft und bezahlt (Nachtrag)<br /><span className="text-xs text-[#71717A]">Es wird keine Rechnung und keine Zahlungsaufforderung erstellt.</span></span>
            </label>
            {f.already_paid ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Bezahlt am</Label><Input type="date" value={f.paid_date} onChange={(e) => set("paid_date")(e.target.value)} className="mt-1 bg-[#0A0A0A]" data-testid="input-cc-paid-date" /></div>
                  <div><Label>Bereits genutzt</Label><Input type="number" min={0} value={f.credits_used} onChange={(e) => set("credits_used")(e.target.value)} className="mt-1 bg-[#0A0A0A]" data-testid="input-cc-used" /></div>
                </div>
                <label className="flex items-center gap-2"><input type="checkbox" checked={f.send_email} onChange={(e) => set("send_email")(e.target.checked)} data-testid="checkbox-cc-send" /> Kartencode per E-Mail an Kunden senden</label>
              </div>
            ) : <p className="text-xs text-[#71717A]">Die Karte wird als „Noch nicht bezahlt“ angelegt. Nach „Zahlung bestätigen“ wird sie aktiviert, die Rechnung erstellt und der Code versendet.</p>}
            <div><Label>Notiz (optional)</Label><Input value={f.note} onChange={(e) => set("note")(e.target.value)} className="mt-1 bg-[#0A0A0A]" /></div>
            <Button onClick={submit} disabled={busy || !f.customer_name || !f.customer_email} className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]" data-testid="btn-cc-submit">
              {busy ? "Wird gespeichert..." : "Karte anlegen"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
